import { useEffect, useRef } from "react";

/* ============================================================
   Visibility-aware polling hook.
   ------------------------------------------------------------
   Calls `callback` every `intervalMs` — BUT ONLY while the tab
   is visible (Page Visibility API: `document.hidden === false`).

   When the tab is hidden (user switches tabs, backgrounds the
   browser on mobile, screen locks), the interval is cleared and
   no callback fires. When visibility returns, the callback runs
   immediately once (so the user doesn't stare at stale prices
   for 25s after coming back) and the interval resumes.

   The callback is held in a ref so consumers can pass an inline
   closure without triggering re-subscribes on every render —
   only the intervalMs and the initial mount matter for setup.
   ============================================================ */
export function useVisiblePoll(callback, intervalMs) {
  const cbRef = useRef(callback);
  useEffect(() => { cbRef.current = callback; }, [callback]);

  useEffect(() => {
    if (!intervalMs || intervalMs <= 0) return;

    let intervalId = null;
    let disposed = false;

    const fireOnce = () => {
      if (disposed) return;
      if (document.hidden) return;
      try { cbRef.current && cbRef.current(); } catch { /* swallow */ }
    };

    const start = () => {
      if (intervalId != null) return;
      intervalId = setInterval(fireOnce, intervalMs);
    };
    const stop = () => {
      if (intervalId != null) { clearInterval(intervalId); intervalId = null; }
    };

    const onVisibility = () => {
      if (document.hidden) {
        stop();
      } else {
        // Immediate refresh on return + resume timer.
        fireOnce();
        start();
      }
    };

    if (!document.hidden) start();
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      disposed = true;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [intervalMs]);
}
